import { createElement, useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AiSettingsCard } from '../components/AiSettingsCard'
import { AiUsageCard } from '../components/AiUsageCard'
import { ReindexCard } from '../components/ReindexCard'
import { EmbeddingDebugCard } from '../components/EmbeddingDebugCard'
import { usePageLayout } from '@/hooks/usePageLayout'
import SettingsSidebar from '@/components/app/sidebars/settings'
import {
  SettingsPageContainer,
  SettingsPageHeader,
} from '@/components/app/layout'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { usePlanFundsAi } from '@/lib/ai/usePlanFundsAi'
import useLoginStore from '@/store/LoginStore'

/**
 * Workspace Settings → AI tab. Four stacked cards:
 *   1. AI Settings — kill-switch toggle + plan tier + quota allocation.
 *   2. AI Usage — current-billing-period spend, broken down by kind / model
 *      / operation. Reads `/ai/admin/usage` so it stays reachable while AI
 *      is disabled (the admin's primary reason for visiting this tab).
 *   3. Reindex — admin trigger for `POST /ai/index`. Drawer walks the user
 *      through the lower-bound estimate and the cost-confirm gate.
 *   4. Embedding Debug — paste a WorkItem/Document ID, render the composed
 *      embedding text live so admins can verify cascade triggers fired.
 *
 * The whole tab is gated behind `MANAGE_AI_SETTINGS` at both the sidebar
 * (entry hidden) and the route loader (redirect for users without the
 * permission). Admins of paid workspaces get it by default; custom roles
 * can be granted it explicitly server-side.
 */
export default function AiSettingsPage() {
  const { t } = useTranslation('settings')
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  // The app root doesn't wrap children in a QueryClientProvider (data loading
  // elsewhere is RxDB-driven), so this page scopes its own client. useState
  // keeps the same instance across re-renders without leaking across routes.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, retry: 1 },
          mutations: { retry: 0 },
        },
      }),
  )
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  // When the plan doesn't fund AI, collapse the tab to just the kill-switch
  // card (toggle + upsell). The usage / reindex / embedding-inspector admin
  // tooling is only meaningful once a plan actually grants an AI budget.
  const planFundsAi = usePlanFundsAi()

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsPageContainer innerClassName="space-y-6">
        <SettingsPageHeader
          title={t('aiSettings.title')}
          description={t('aiSettings.description')}
        />

        <AiSettingsCard workspaceSlug={workspaceSlug} />
        {planFundsAi && (
          <>
            <AiUsageCard workspaceSlug={workspaceSlug} />
            <ReindexCard workspaceSlug={workspaceSlug} />
            <EmbeddingDebugCard workspaceSlug={workspaceSlug} />
          </>
        )}
      </SettingsPageContainer>
    </QueryClientProvider>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/ai',
)({
  // Gate before the page mounts so users without MANAGE_AI_SETTINGS never see
  // the page chrome at all. We re-read from the store here (not via the hook)
  // because beforeLoad runs outside React.
  beforeLoad: ({ params }) => {
    const store = useLoginStore.getState()
    const hasPermission =
      // Same fallback policy as useHasPermission — see useWorkspaceRole.ts.
      store.userPermissionsLoaded
        ? store.userPermissions.includes(WORKSPACE_PERMISSIONS.MANAGE_AI_SETTINGS)
        : store.userRole === 'admin'
    if (!hasPermission) {
      throw redirect({ to: '/$workspaceSlug', params })
    }
  },
  component: AiSettingsPage,
})
